/*
 * Deskon - homepage hero, mobile only. Three measurements:
 *
 *   1. the headline is sized so its line fills the space between the page margins, on one line;
 *   2. the button is given that same span, so both start and end on those margins;
 *   3. the offer sentence is sized so its two lines are exactly as TALL as the "-30%" beside it,
 *      never wider than the space left, and offset so its ink sits on the numeral's ink.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THE HEADLINE IS ALIGNED BY ITS BOX, NOT ITS INK - the mistake that cost ten passes
 * ---------------------------------------------------------------------------------------------
 * The brief was that the headline start on the same vertical line as "Shop by category" and every
 * other title on the homepage. A line of text is narrower than its layout box, because each glyph
 * carries side bearings, so several versions of this file measured the INK and enlarged the
 * headline until the letters themselves reached the margin.
 *
 * That was chasing the wrong thing. "Shop by category" is set in the same typeface and sits in a
 * plain box on the page margin - so ITS ink is inset by a side bearing too. Correcting the hero's
 * headline made it stick out FURTHER than the reference, not line up with it. The closer the
 * measurement got to "perfect", the more wrong the result looked.
 *
 * Aligning boxes reproduces exactly what the rest of the page does, and the two inks land in the
 * same place because they are the same shapes in the same font. The advance width also comes from
 * a DOM probe, which is what the browser will actually lay out - dependable, unlike canvas ink
 * metrics, which silently collapse when the canvas fails to resolve the page's font.
 *
 * Ink metrics are still used for the offer sentence, where the comparison is between two texts on
 * the same line and the bearings genuinely differ.
 *
 * ---------------------------------------------------------------------------------------------
 * THE em/px TRAP - worth keeping in mind for any measurement added here
 * ---------------------------------------------------------------------------------------------
 * Both texts are letter-spaced in em, but getComputedStyle reports that resolved to PIXELS at the
 * size currently rendered. Every measurement here is taken at a REFERENCE_SIZE of 100px, so
 * passing those pixels through unchanged makes the spacing proportionally far too tight - which
 * made the headline about 10% too small until it was caught. See toEm() and scaledLetterSpacing().
 *
 * ---------------------------------------------------------------------------------------------
 * TWO CONSTRAINTS THAT FIGHT, on the sentence
 * ---------------------------------------------------------------------------------------------
 * It must not cross the right edge, and it must stand as tall as the numeral. The width beside
 * the numeral may be too narrow for type that tall, so the smaller of the two candidate sizes
 * always wins - overflow is impossible - and the leading is then SOLVED from whatever size
 * survived, so the height still comes out right.
 *
 * DESKTOP IS UNTOUCHED. Every property is removed above 750px. That matters because these are
 * INLINE custom properties, which outrank every stylesheet rule including a media query.
 */

const HERO = '.hero-split-wrapper';
const COPY = '.hero-split-wrapper .hero__content-wrapper';
const OFFER_ROW =
  '.hero-split-wrapper__promo .color-scheme-deskon2-ink > .group-block-content > :nth-child(3) > .group-block-content';

const SIZE_PROPERTY = '--hero-display-size';
const CTA_WIDTH_PROPERTY = '--hero-cta-width';
const OFFER_SIZE_PROPERTY = '--hero-offer-size';
const OFFER_LH_PROPERTY = '--hero-offer-lh';
const OFFER_MAX_PROPERTY = '--hero-offer-max';
const OFFER_SHIFT_PROPERTY = '--hero-offer-shift';

const MOBILE_MAX = 749;
const REFERENCE_SIZE = 100;

const MIN_SIZE = 20;
const MAX_SIZE = 96;

const PREFERRED_LEADING = 1.18;
const MIN_LEADING = 1.02;
const MAX_LEADING = 1.8;
const MIN_OFFER_SIZE = 11;

const FONT_PROPERTIES = ['fontFamily', 'fontWeight', 'fontStyle', 'fontStretch', 'fontVariant', 'textTransform'];

/** px -> em against a known font-size, for the DOM probe. */
function toEm(value, fontSize) {
  const pixels = parseFloat(value);
  if (!Number.isFinite(pixels) || !fontSize) return 'normal';
  return `${pixels / fontSize}em`;
}

/** The same conversion for canvas, which takes a length: rescaled to REFERENCE_SIZE. */
function scaledLetterSpacing(value, fontSize) {
  const pixels = parseFloat(value);
  if (!Number.isFinite(pixels) || !fontSize) return '0px';
  return `${(pixels / fontSize) * REFERENCE_SIZE}px`;
}

/** Advance width of a string at REFERENCE_SIZE, in the styling of `source`, measured off-layout. */
function measureText(source, text) {
  const styles = getComputedStyle(source);
  const currentSize = parseFloat(styles.fontSize) || 16;
  const probe = document.createElement('span');

  FONT_PROPERTIES.forEach((property) => {
    probe.style[property] = styles[property];
  });

  probe.style.letterSpacing = toEm(styles.letterSpacing, currentSize);
  probe.style.wordSpacing = toEm(styles.wordSpacing, currentSize);
  probe.style.position = 'absolute';
  probe.style.left = '-99999px';
  probe.style.top = '0';
  probe.style.visibility = 'hidden';
  probe.style.whiteSpace = 'nowrap';
  probe.style.fontSize = `${REFERENCE_SIZE}px`;
  probe.textContent = text;

  document.body.appendChild(probe);
  const width = probe.getBoundingClientRect().width;
  probe.remove();

  return width;
}

/** Glyph metrics for a string, per unit of font-size. Null where the browser will not report them. */
function measureMetrics(source, text) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;

  const styles = getComputedStyle(source);
  const currentSize = parseFloat(styles.fontSize) || 16;

  if ('letterSpacing' in context) {
    context.letterSpacing = scaledLetterSpacing(styles.letterSpacing, currentSize);
  }
  context.font = `${styles.fontStyle} ${styles.fontWeight} ${REFERENCE_SIZE}px ${styles.fontFamily}`;

  const metrics = context.measureText(text);
  if (metrics.actualBoundingBoxAscent === undefined || metrics.fontBoundingBoxAscent === undefined) {
    return null;
  }

  return {
    inkAscent: metrics.actualBoundingBoxAscent / REFERENCE_SIZE,
    inkDescent: metrics.actualBoundingBoxDescent / REFERENCE_SIZE,
    fontAscent: metrics.fontBoundingBoxAscent / REFERENCE_SIZE,
    fontDescent: metrics.fontBoundingBoxDescent / REFERENCE_SIZE,
  };
}

/** The most balanced two-line split of a string: the two lines, and the wider one's width. */
function splitIntoTwoLines(source) {
  const words = source.textContent.trim().split(/\s+/);
  if (words.length < 2) {
    return { first: words.join(' '), second: '', widest: measureText(source, words.join(' ')) };
  }

  let best = null;

  for (let split = 1; split < words.length; split += 1) {
    const first = words.slice(0, split).join(' ');
    const second = words.slice(split).join(' ');
    const widest = Math.max(measureText(source, first), measureText(source, second));
    if (!best || widest < best.widest) best = { first, second, widest };
  }

  return best;
}

/** How far below an element's box top its ink begins, per unit of font-size. */
function inkTopOffset(metrics, leading) {
  const halfLeading = (leading - (metrics.fontAscent + metrics.fontDescent)) / 2;
  return halfLeading + metrics.fontAscent - metrics.inkAscent;
}

function clearAll(hero) {
  [
    SIZE_PROPERTY,
    CTA_WIDTH_PROPERTY,
    OFFER_SIZE_PROPERTY,
    OFFER_LH_PROPERTY,
    OFFER_MAX_PROPERTY,
    OFFER_SHIFT_PROPERTY,
  ].forEach((property) => hero.style.removeProperty(property));
}

function fitOffer(hero, displaySize, blockWidth) {
  const row = document.querySelector(OFFER_ROW);
  if (!row) return;

  const numeral = row.children[0];
  const sentence = row.children[1];
  if (!numeral || !sentence) return;

  const numeralText = numeral.textContent.trim();
  const numeralMetrics = measureMetrics(numeral, numeralText);
  const lines = splitIntoTwoLines(sentence);
  const firstMetrics = measureMetrics(sentence, lines.first);
  const secondMetrics = measureMetrics(sentence, lines.second || lines.first);

  if (!numeralMetrics || !firstMetrics || !secondMetrics || !(lines.widest > 0)) return;

  const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
  const numeralWidth = (measureText(numeral, numeralText) * displaySize) / REFERENCE_SIZE;
  const available = blockWidth - numeralWidth - gap;
  if (!(available > 0)) return;

  const numeralLeading = (parseFloat(getComputedStyle(numeral).lineHeight) || displaySize) / displaySize;
  const targetInkHeight = (numeralMetrics.inkAscent + numeralMetrics.inkDescent) * displaySize;
  const numeralInkTop = inkTopOffset(numeralMetrics, numeralLeading) * displaySize;

  const span = (leading) => firstMetrics.inkAscent + leading + secondMetrics.inkDescent;

  const sizeFromHeight = targetInkHeight / span(PREFERRED_LEADING);
  const sizeFromWidth = (REFERENCE_SIZE * available) / lines.widest;
  const size = Math.max(MIN_OFFER_SIZE, Math.min(sizeFromHeight, sizeFromWidth));

  const solvedLeading = targetInkHeight / size - firstMetrics.inkAscent - secondMetrics.inkDescent;
  const leading = Math.max(MIN_LEADING, Math.min(MAX_LEADING, solvedLeading));

  const shift = numeralInkTop - inkTopOffset(firstMetrics, leading) * size;

  hero.style.setProperty(OFFER_SIZE_PROPERTY, `${Math.round(size * 100) / 100}px`);
  hero.style.setProperty(OFFER_LH_PROPERTY, `${Math.round(leading * 1000) / 1000}`);
  hero.style.setProperty(OFFER_MAX_PROPERTY, `${Math.floor(available)}px`);
  hero.style.setProperty(OFFER_SHIFT_PROPERTY, `${Math.round(shift * 100) / 100}px`);
}

function fitHeadline() {
  const hero = document.querySelector(HERO);
  const copy = document.querySelector(COPY);
  if (!hero || !copy) return;

  if (window.innerWidth > MOBILE_MAX) {
    clearAll(hero);
    return;
  }

  const heading = copy.querySelector('h1, h2');
  if (!heading) return;

  const text = heading.textContent.trim();
  const styles = getComputedStyle(copy);
  const available =
    copy.clientWidth - parseFloat(styles.paddingLeft || 0) - parseFloat(styles.paddingRight || 0);
  if (!(available > 0)) return;

  const advance = measureText(heading, text);
  if (!(advance > 0)) return;

  /* One rule, no correction: the line's box fills the space between the margins, exactly as a
   * plain section title does. Its box therefore starts on the margin, and so does the button. */
  const size = Math.max(
    MIN_SIZE,
    Math.min(MAX_SIZE, Math.floor(((REFERENCE_SIZE * available) / advance) * 10) / 10)
  );

  hero.style.setProperty(SIZE_PROPERTY, `${size}px`);
  hero.style.setProperty(CTA_WIDTH_PROPERTY, `${Math.round(available)}px`);

  fitOffer(hero, size, Math.round(available));
}

let queued = false;

function schedule() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    fitHeadline();
  });
}

schedule();

window.addEventListener('resize', schedule);
window.addEventListener('load', schedule);

/* Web fonts change every measurement - the first pass runs against the fallback face. */
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(schedule);
}

document.addEventListener('shopify:section:load', schedule);
